import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Space, Tooltip, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import rbacService from '../../services/rbacService';

export default function LevelsManagement() {
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLevel, setEditingLevel] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchLevels();
    }, []);

    const fetchLevels = async () => {
        setLoading(true);
        try {
            const result = await rbacService.getHierarchyLevels();
            if (result.success) {
                // Sort by level number ascending (1, 2, 10...)
                const sorted = [...result.data].sort((a, b) => a.level - b.level);
                setLevels(sorted);
            } else {
                message.error(result.message || 'Failed to fetch levels');
            }
        } catch (error) {
            console.error('Error fetching levels:', error);
            message.error('Internal Server Error');
        } finally {
            setLoading(false);
        }
    };

    const showModal = (record = null) => {
        setEditingLevel(record);
        if (record) {
            form.setFieldsValue(record);
        } else {
            form.resetFields();
        }
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setEditingLevel(null);
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            let result;
            if (editingLevel) {
                result = await rbacService.updateLevel(editingLevel.id, values);
            } else {
                result = await rbacService.createLevel(values);
            }

            if (result.success) {
                message.success(result.message);
                setIsModalOpen(false);
                fetchLevels();
            } else {
                message.error(result.message || 'Operation failed');
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const result = await rbacService.deleteLevel(id);
            if (result.success) {
                message.success(result.message);
                fetchLevels();
            } else {
                message.error(result.message || 'Deletion failed');
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Cannot delete level');
        }
    };

    const columns = [
        {
            title: 'Level Rank',
            dataIndex: 'level',
            key: 'level',
            render: (level) => (
                <span className="font-mono font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                    {level}
                </span>
            ),
            sorter: (a, b) => a.level - b.level,
            width: 120,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name) => <span className="font-semibold text-slate-800">{name}</span>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            className: 'text-slate-500',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Edit Level">
                        <Button
                            type="text"
                            icon={<EditOutlined className="text-blue-600" />}
                            onClick={() => showModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete Level">
                        <Popconfirm
                            title="Delete Level"
                            description="Are you sure? This might affect roles assigned to this level."
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6 flex justify-between items-center bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                    <InfoCircleOutlined className="text-slate-400" />
                    <p className="text-sm text-slate-600 m-0">
                        Hierarchy levels define the reporting authority. Lower rank numbers have higher authority.
                    </p>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => showModal()}
                    className="bg-indigo-600 hover:bg-indigo-700"
                >
                    Add New Level
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={levels}
                rowKey="id"
                loading={loading}
                pagination={false}
                className="border border-slate-100 rounded-lg overflow-hidden shadow-sm"
            />

            <Modal
                title={editingLevel ? 'Edit Hierarchy Level' : 'Create New Hierarchy Level'}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    className="mt-4"
                >
                    <Form.Item
                        name="level"
                        label="Level Rank (Number)"
                        rules={[{ required: true, message: 'Rank is required' }]}
                        extra="Managers are determined by having a lower level rank than subordinates."
                    >
                        <InputNumber
                            min={1}
                            max={1000}
                            className="w-full"
                            placeholder="e.g., 25"
                            disabled={!!editingLevel} // Rank usually shouldn't change for existing levels to prevent hierarchy breakage
                        />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Level Name"
                        rules={[{ required: true, message: 'Name is required' }]}
                    >
                        <Input placeholder="e.g., Senior Management" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Description is required' }]}
                    >
                        <Input.TextArea rows={3} placeholder="Describe the responsibilities of this level..." />
                    </Form.Item>

                    <Form.Item className="mb-0 flex justify-end">
                        <Space>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={loading} className="bg-indigo-600">
                                {editingLevel ? 'Update Level' : 'Create Level'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
